import { useCallback, useEffect, useState } from "react";
import type { ComponentType } from "react";

// react-router components
import { Route, Switch, Redirect, useLocation } from "react-router-dom";
import type { RouteComponentProps } from "react-router-dom";

// @mui material components
import Icon from "@mui/material/Icon";

// Vision UI Dashboard PRO React components
import VuiBox from "components/VuiBox";

// Vision UI Dashboard PRO React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

import RouteSuspense from "layouts/common-components/RouteSuspense";

// Vision UI Dashboard PRO React routes
import routes from "routes";

// Vision UI Dashboard PRO React contexts
import { useVisionUIController, setMiniSidenav } from "context";
import { useAuth } from "context/app";

// Plugins custom css
import "assets/theme/base/plugins.css";

import "assets/custom-style/style.css";

// The parts of a routes.js entry that become a <Route>: a page, or a group of them.
interface AppRoute {
  key?: string;
  route?: string;
  // Any page: each one types the `data` it takes itself.
  component?: ComponentType<never>;
  // Passed through to the page; routes.js pairs each page with the data it takes.
  data?: unknown;
  collapse?: AppRoute[];
}

// Flat and stable: the route list never changes at runtime, so there is no reason to
// rebuild these elements on every render.
const renderRoutes = (allRoutes: AppRoute[]): JSX.Element[] =>
  allRoutes.flatMap((route) => {
    if (route.collapse) {
      return renderRoutes(route.collapse);
    }

    // Every entry with a route has a page.
    if (route.route && route.component) {
      // routes.js gives each page the data it takes, so it can be handed over as is.
      const Page = route.component as ComponentType<RouteComponentProps & { data?: unknown }>;

      return [
        <Route
          exact
          path={route.route}
          key={route.key}
          render={(props) => <Page {...props} data={route.data} />}
        />,
      ];
    }

    return [];
  });

const routeElements = renderRoutes(routes);

const configsButton = (
  <VuiBox
    display="flex"
    justifyContent="center"
    alignItems="center"
    width="3.5rem"
    height="3.5rem"
    bgColor="white"
    shadow="sm"
    borderRadius="50%"
    position="fixed"
    right="2rem"
    bottom="2rem"
    zIndex={99}
    color="white"
    sx={({ palette: { info } }) => ({ cursor: "pointer", backgroundColor: info.main })}
  >
    <Icon fontSize="inherit" color="inherit">
      settings
    </Icon>
  </VuiBox>
);

export default function App() {
  const [controller, dispatch] = useVisionUIController();
  const { miniSidenav, direction, layout, sidenavColor } = controller;
  const { isAuthorized } = useAuth();
  const { pathname } = useLocation();

  const [onMouseEnter, setOnMouseEnter] = useState(false);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = useCallback(() => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  }, [miniSidenav, onMouseEnter, dispatch]);

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = useCallback(() => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  }, [onMouseEnter, dispatch]);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    }
  }, [pathname]);

  return (
    <>
      {layout === "dashboard" && (
        <>
          {isAuthorized && (
            <Sidenav
              color={sidenavColor}
              brandName="Efficientsoftware"
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
          )}
          <Configurator />
          {configsButton}
        </>
      )}
      <RouteSuspense>
        <Switch>
          {routeElements}
          {isAuthorized ? (
            <Redirect exact from="/" to="/dashboard" />
          ) : (
            <Redirect from="*" to="/sign-in" />
          )}
        </Switch>
      </RouteSuspense>
    </>
  );
}
